import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Phone, MessageSquare, Mail, MapPin, ArrowUpRight } from "lucide-react";
import { Container, Button } from "@/components/primitives";
import { useBooking } from "@/lib/booking";
import { business, services, agencyUrl } from "@/data/site";

const legal = [
  { label: "Terms & Conditions", to: "/terms" },
  { label: "Privacy Policy", to: "/privacy" },
  { label: "Accessibility", to: "/accessibility" },
];

export const Footer = () => {
  const { openBooking } = useBooking();
  const year = new Date().getFullYear();

  return (
    <footer className="relative z-10 border-t border-[var(--border)] bg-[var(--surface)]">
      <Container className="py-16 sm:py-20">
        <div className="flex flex-col gap-6 border-b border-[var(--border)] pb-14 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <span className="font-mono text-xs uppercase tracking-[0.28em] text-[var(--primary)]">
              Ready when you are
            </span>
            <h2 className="mt-4 font-heading text-4xl font-light leading-[1.05] tracking-tight text-[var(--fg)] sm:text-5xl">
              Your best smile starts in Hell's Kitchen.
            </h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button size="lg" onClick={() => openBooking()} data-testid="footer-book-btn">
              Book Appointment
            </Button>
            <Button size="lg" variant="outline" href={business.phoneHref} data-testid="footer-call-btn">
              <Phone size={16} /> Call / Text Us
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-10 py-14 md:grid-cols-4 lg:grid-cols-5">
          <div className="col-span-2 lg:col-span-2">
            <span className="font-heading text-2xl text-[var(--fg)]">Meridian Dental Studio</span>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-[var(--muted-fg)]">
              A modern, full-service dental practice serving Hell's Kitchen, Midtown West, Chelsea and greater Manhattan with preventative, cosmetic, restorative and orthodontic care.
            </p>
            <div className="mt-6 flex gap-3">
              {business.social.instagram && (
                <a href={business.social.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" aria-label="Instagram" className="flex h-10 w-10 items-center justify-center rounded-full border border-[var(--border)] text-[var(--fg)] transition-colors hover:border-[var(--primary)] hover:text-[var(--primary)]">
                  <Instagram size={17} />
                </a>
              )}
              {business.social.facebook && (
                <a href={business.social.facebook} target="_blank" rel="noopener noreferrer" data-testid="footer-facebook" aria-label="Facebook" className="flex h-10 w-10 items-center justify-center rounded-full border border-[var(--border)] text-[var(--fg)] transition-colors hover:border-[var(--primary)] hover:text-[var(--primary)]">
                  <Facebook size={17} />
                </a>
              )}
            </div>
          </div>

          <div>
            <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--muted-fg)]">Services</h3>
            <ul className="mt-5 space-y-3">
              {services.map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} className="link-underline text-sm text-[var(--fg)]/80 hover:text-[var(--primary)]">
                    {s.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--muted-fg)]">Explore</h3>
            <ul className="mt-5 space-y-3">
              {[
                { label: "About & Team", to: "/about" },
                { label: "Smile Gallery", to: "/smile-gallery" },
                { label: "Insurance & Financing", to: "/insurance-financing" },
                { label: "FAQ & Emergency", to: "/faq" },
                { label: "Contact & Location", to: "/contact" },
              ].map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="link-underline text-sm text-[var(--fg)]/80 hover:text-[var(--primary)]">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--muted-fg)]">Visit Us</h3>
            <ul className="mt-5 space-y-4 text-sm text-[var(--fg)]/80">
              <li className="flex gap-3">
                <MapPin size={16} className="mt-0.5 shrink-0 text-[var(--primary)]" />
                <span>{business.address1}<br />{business.address2}</span>
              </li>
              <li>
                <a href={business.phoneHref} data-testid="footer-phone" className="flex items-center gap-3 hover:text-[var(--primary)]">
                  <Phone size={16} className="text-[var(--primary)]" /> {business.phoneDisplay}
                </a>
              </li>
              <li>
                <a href={business.smsHref} data-testid="footer-text" className="flex items-center gap-3 hover:text-[var(--primary)]">
                  <MessageSquare size={16} className="text-[var(--primary)]" /> Text Us
                </a>
              </li>
              <li>
                <a href={business.emailHref} data-testid="footer-email" className="flex items-center gap-3 hover:text-[var(--primary)]">
                  <Mail size={16} className="text-[var(--primary)]" /> {business.email}
                </a>
              </li>
              <li>
                <a href={business.directionsHref} target="_blank" rel="noopener noreferrer" data-testid="footer-directions" className="inline-flex items-center gap-1 text-[var(--primary)] hover:underline">
                  Get Directions <ArrowUpRight size={14} />
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col gap-4 border-t border-[var(--border)] pt-8 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-[var(--muted-fg)]">
            © {year} Meridian Dental Studio (concept).{" "}
            <a
              href={agencyUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-agency-link"
              className="text-[var(--primary)] transition-colors hover:underline"
            >
              Created by Ananta Digital Works
            </a>
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {legal.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`footer-${l.to.replace("/", "")}`} className="text-xs text-[var(--muted-fg)] transition-colors hover:text-[var(--primary)]">
                {l.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-8 rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4" data-testid="footer-demo-disclaimer">
          <p className="max-w-4xl text-xs leading-relaxed text-[var(--muted-fg)]">
            <strong className="text-[var(--fg)]">DEMO SITE ONLY:</strong> This concept website was created by{" "}
            <a href={agencyUrl} target="_blank" rel="noopener noreferrer" className="text-[var(--primary)] hover:underline">
              Ananta Digital Works
            </a>{" "}
            for portfolio and demonstration purposes only. Meridian Dental Studio is a fictional concept; this website is not affiliated with, endorsed by, or accepting appointment requests, patient intakes, or medical emergencies for any live business.
          </p>
        </div>
      </Container>
    </footer>
  );
};
